import React, { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Drawer,
  Modal,
  Text,
  UnstyledButton,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconChevronDown,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import type { CalendarEventType } from "../../../../../types/calendar";
import type { Employee, SalonService } from "../../../../../types/salon";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import ClientForm from "../../../../clients/ClientForm";
import { serviceQuote } from "../../booking/draftModel";
import { draftOverlap } from "../../booking/draftOverlap";
import { useNextBlocker } from "../../booking/useNextBlocker";
import { MAX_SUGGESTIONS, slotLabel, slotParts } from "../detail/detailModel";
import { initialsOf } from "../grid/geometry";
import { StaffFace, staffPhotoOf } from "../grid/StaffFace";
import { SHEET_TRANSITION, sheetMotion } from "../sheets/MobileSheet";
import { MOBILE_Z } from "../tokens";
import {
  ToastCopy,
  clashBarText,
  draftFootHint,
  nameInitials,
  rowStaffId,
} from "./bookingSheetModel";
import { MobileBookingReview } from "./MobileBookingReview";
import { CoverRow, MobileCoverPicker } from "./MobileCoverPicker";
import { MobileServiceCard } from "./MobileServiceCard";
import {
  MobileDraftSession,
  useMobileBookingDraft,
} from "./useMobileBookingDraft";
import styles from "./MobileBooking.module.css";

/** Drag distance (px) past which the sheet asks to close (prototype). */
export const DRAG_DISMISS_PX = 120;

export interface MobileNewAppointmentSheetProps {
  /** The shared booking session (useBookingSession); open ⇔ sheetOpen. */
  session: MobileDraftSession;
  services: SalonService[];
  /** Bookable active staff. */
  staff: Employee[];
  /** Calendar events (all staff) for conflicts. */
  events: CalendarEventType[];
  timezone: string;
  /** Created: the toast copy (the session already stored + closed). */
  onBooked: (toast: ToastCopy) => void;
}

type Picker =
  | { kind: "client" }
  | { kind: "service"; uid: number }
  | { kind: "staff"; uid: number }
  | null;

const SheetBody: React.FC<MobileNewAppointmentSheetProps> = ({
  session,
  services,
  staff,
  events,
  timezone,
  onBooked,
}) => {
  const draft = useMobileBookingDraft({
    session,
    services,
    staff,
    events,
    timezone,
  });

  const [step, setStep] = useState<"edit" | "review">("edit");
  const [picker, setPicker] = useState<Picker>(null);
  const [clientQuery, setClientQuery] = useState("");
  const [durOpenUid, setDurOpenUid] = useState<number | null>(null);
  const [adjClosed, setAdjClosed] = useState<Record<number, boolean>>({});
  const [removingUid, setRemovingUid] = useState<number | null>(null);
  const [clashBar, setClashBar] = useState(false);
  const [clashOk, setClashOk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);

  // Escape order (README): discard confirmation → full-cover picker → sheet.
  // Latest values for the one document listener.
  const escRef = useRef({
    discard: session.discardPromptOpen,
    picker,
    creatingClient,
    requestClose: session.requestClose,
  });
  escRef.current = {
    discard: session.discardPromptOpen,
    picker,
    creatingClient,
    requestClose: session.requestClose,
  };
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const {
        discard,
        picker: open,
        creatingClient: creating,
        requestClose,
      } = escRef.current;
      // The discard / new client modals handle their own Escape.
      if (discard || creating) return;
      e.preventDefault();
      if (open) setPicker(null);
      else requestClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Drag handle: fling down past DRAG_DISMISS_PX → the discard gate.
  const dragCleanup = useRef<(() => void) | null>(null);
  useEffect(() => () => dragCleanup.current?.(), []);
  const onHandlePointerDown = (e: React.PointerEvent) => {
    const y0 = e.clientY;
    const onMove = (ev: PointerEvent) => setDragY(Math.max(0, ev.clientY - y0));
    const onUp = (ev: PointerEvent) => {
      dragCleanup.current?.();
      setDragging(false);
      setDragY(0);
      if (ev.clientY - y0 > DRAG_DISMISS_PX) escRef.current.requestClose();
    };
    dragCleanup.current = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      dragCleanup.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    setDragging(true);
  };

  // ── Step transitions ──────────────────────────────────────────────────
  const goReview = () => {
    setError(null);
    const err = draft.prepareReview();
    if (err === null) {
      setStep("review");
      setClashBar(false);
      setDurOpenUid(null);
      setRemovingUid(null);
    } else if (err) {
      setError(err);
    }
  };
  const handleNext = () => {
    const blocker = nextBlocker.reveal();
    if (blocker) {
      if (blocker.uid !== null) draft.setActive(blocker.uid);
      return;
    }
    if (draft.clashRows.length && !clashOk) {
      setClashBar(true);
      return;
    }
    goReview();
  };
  const handleClashContinue = () => {
    setClashBar(false);
    setClashOk(true);
    goReview();
  };
  const handleConfirm = async () => {
    setError(null);
    const result = await draft.submit();
    if ("toast" in result) onBooked(result.toast);
    else if (result.error) setError(result.error);
  };

  // ── Pickers ───────────────────────────────────────────────────────────
  const pickerRow =
    picker && picker.kind !== "client"
      ? draft.rows.find((r) => r.uid === picker.uid) || null
      : null;

  const renderPicker = () => {
    if (!picker) return null;
    const close = () => setPicker(null);
    if (picker.kind === "client") {
      const options = draft.clientOptions(clientQuery);
      return (
        <MobileCoverPicker
          title="Select client"
          onClose={close}
          search={{
            value: clientQuery,
            onChange: setClientQuery,
            placeholder: "Search clients",
          }}
          footer={
            <UnstyledButton
              className={styles.coverFootBtn}
              onClick={() => setCreatingClient(true)}
            >
              <IconPlus size={16} stroke={1.8} />
              <Box component="span">Add a new client</Box>
            </UnstyledButton>
          }
          data-testid="mobile-client-picker"
        >
          {options.map((c) => (
            <UnstyledButton
              key={c.id}
              className={styles.clientRow}
              onClick={() => {
                draft.setClient(c.id);
                setClientQuery("");
                close();
              }}
            >
              {c.name}
            </UnstyledButton>
          ))}
          {clientQuery.trim() && options.length === 0 && (
            <Text className={styles.coverEmpty}>No matching clients</Text>
          )}
        </MobileCoverPicker>
      );
    }
    if (!pickerRow) return null;
    if (picker.kind === "service") {
      return (
        <MobileCoverPicker
          title="Select service"
          onClose={close}
          data-testid="mobile-service-picker"
        >
          {pickerRow.salonServiceId && (
            <CoverRow
              kind="service"
              selected={false}
              onPick={() => {
                draft.setService(pickerRow.uid, "");
                close();
              }}
              label="No service"
            />
          )}
          {draft.serviceOptions(pickerRow).map((s) => {
            // Price / length with this card's provider (their overrides).
            const quote = serviceQuote(s, pickerRow.employeeId);
            return (
              <CoverRow
                key={s.id}
                kind="service"
                selected={pickerRow.salonServiceId === s.id}
                onPick={() => {
                  draft.setService(pickerRow.uid, s.id);
                  close();
                }}
                leading={
                  <Box
                    component="span"
                    className={styles.pickDot}
                    style={
                      s.default_calendar_event_color
                        ? { background: s.default_calendar_event_color }
                        : undefined
                    }
                  />
                }
                label={s.name}
                trailing={
                  <Box component="span" className={styles.pickPrice}>
                    {`${quote.minutes} min · ${formatCurrency(quote.price)}`}
                  </Box>
                }
              />
            );
          })}
        </MobileCoverPicker>
      );
    }
    // Provider: qualified staff.
    return (
      <MobileCoverPicker
        title="Select provider"
        onClose={close}
        data-testid="mobile-staff-picker"
      >
        {pickerRow.employeeId && (
          <CoverRow
            kind="staff"
            selected={false}
            onPick={() => {
              draft.setStaff(pickerRow.uid, "");
              close();
            }}
            label="No provider"
          />
        )}
        {draft.staffOptions(pickerRow).map((e) => (
          <CoverRow
            key={e.id}
            kind="staff"
            selected={pickerRow.employeeId === e.id}
            onPick={() => {
              draft.setStaff(pickerRow.uid, e.id);
              close();
            }}
            leading={
              <Box component="span" className={styles.pickAvatar}>
                <StaffFace
                  photo={staffPhotoOf(e)}
                  initials={initialsOf(e.first_name, e.last_name)}
                />
              </Box>
            }
            label={`${e.first_name ?? ""} ${e.last_name ?? ""}`.trim()}
          />
        ))}
      </MobileCoverPicker>
    );
  };

  // ── Suggested times (all chosen services in order, server order) ─────
  // A slot's start lands on the first chosen service.
  const lead = draft.rows.find((r) => r.salonServiceId) || null;
  const shown = draft.suggestions.slice(0, MAX_SUGGESTIONS);
  const suggMinutes = shown[0]?.duration_mins ?? draft.totals.minutes;
  const renderSuggestions = () => {
    if (!lead || !draft.hasSuggestionQuery || draft.suggestionsError) {
      return null;
    }
    return (
      <Box className={styles.sugWrap} data-testid="mobile-suggested-times">
        <Box className={styles.sugHead}>
          <Text component="span" className={styles.microLabel}>
            Suggested times
          </Text>
          <Text component="span" className={styles.sugRange}>
            {shown.length ? `${suggMinutes} min total` : ""}
          </Text>
        </Box>
        <Box className={styles.sugList}>
          {shown.map((s) => {
            const p = slotParts(s, timezone);
            return (
              <UnstyledButton
                key={`${s.start_dt}-${(s.staff_service_ids || []).join(",")}`}
                className={styles.sugChip}
                data-on={p.day === lead.date && p.time === lead.time}
                onClick={() => draft.applySuggestion(s)}
              >
                {slotLabel(s, timezone)}
              </UnstyledButton>
            );
          })}
        </Box>
        {!draft.suggestionsLoading && shown.length === 0 && (
          <Text className={styles.sugEmpty}>
            No open times in that range. Try a wider range.
          </Text>
        )}
      </Box>
    );
  };

  // One overlap warning for the draft (warning only), as the desktop drawer.
  const overlap = draftOverlap(draft.rows, events, {
    serviceName: (r) => draft.serviceOf(r)?.name || "",
  });
  // Next stays clickable: clicking it on an incomplete draft shows why.
  const nextBlocker = useNextBlocker(draft.rows, draft.clientUuid, services);
  const shownBlocker = step === "edit" ? nextBlocker.shown : null;

  const clashNames = draft.clashRows.map((r) => draft.staffName(rowStaffId(r)));
  const footHint = draftFootHint(
    draft.rows,
    !!draft.clientUuid,
    draft.bookable,
    draft.totals,
  );

  return (
    <Box
      className={styles.panel}
      style={{
        transform: `translateY(${dragY}px)`,
        transition: dragging ? "none" : "transform .2s ease",
      }}
      data-testid="mobile-new-appointment-sheet"
    >
      <Box className={styles.handleZone} onPointerDown={onHandlePointerDown}>
        <Box
          component="span"
          className={styles.handle}
          data-testid="mobile-sheet-handle"
        />
      </Box>
      <Box className={styles.head}>
        <Box className={styles.headText}>
          <Text className={styles.title}>New appointment</Text>
          <Text className={styles.subtitle}>
            {step === "review"
              ? "Check the details, then confirm"
              : "Pick a service, provider and time for each service"}
          </Text>
        </Box>
        <ActionIcon
          variant="default"
          className={styles.iconBtn}
          onClick={session.requestClose}
          aria-label="Close"
        >
          <IconX size={18} stroke={1.8} />
        </ActionIcon>
      </Box>

      <Box className={styles.panelBody}>
        {step === "edit" ? (
          <Box className={styles.body}>
            {/* Client first (desktop drawer's gate): services stay hidden
                until a client is chosen (Next says so if clicked first). */}
            <Text className={styles.microLabel}>Client</Text>
            <UnstyledButton
              className={styles.clientPill}
              data-open={picker?.kind === "client"}
              data-error={nextBlocker.flags(null, "client")}
              onClick={() => {
                setClientQuery("");
                setPicker({ kind: "client" });
              }}
            >
              <Box component="span" className={styles.clientDot}>
                {nameInitials(draft.clientName)}
              </Box>
              <Box
                component="span"
                className={styles.clientLabel}
                data-set={!!draft.clientUuid}
              >
                {draft.clientName || "Add client"}
              </Box>
              <Box component="span" className={styles.chev}>
                <IconChevronDown size={16} stroke={1.8} />
              </Box>
            </UnstyledButton>

            {!draft.clientUuid && (
              <Text
                className={styles.gateNote}
                data-testid="mobile-client-gate"
              >
                Choose a client first, then add services.
              </Text>
            )}
            {draft.clientUuid && (
              <>
                <Text className={styles.microLabel}>Services</Text>
                <Box className={styles.cards}>
                  {draft.rows.map((row, index) => (
                    <MobileServiceCard
                      key={row.uid}
                      draft={draft}
                      row={row}
                      index={index}
                      removing={removingUid === row.uid}
                      durOpen={durOpenUid === row.uid}
                      adjOpen={!adjClosed[row.uid]}
                      onTrash={() => {
                        setRemovingUid(row.uid);
                        draft.setActive(row.uid);
                      }}
                      onRemoveCancel={() => setRemovingUid(null)}
                      onRemoveConfirm={() => {
                        setRemovingUid(null);
                        draft.removeRow(row.uid);
                      }}
                      onPickService={() =>
                        setPicker({ kind: "service", uid: row.uid })
                      }
                      onPickStaff={() =>
                        setPicker({ kind: "staff", uid: row.uid })
                      }
                      onToggleDur={() =>
                        setDurOpenUid((cur) =>
                          cur === row.uid ? null : row.uid,
                        )
                      }
                      invalidField={
                        shownBlocker?.uid === row.uid
                          ? shownBlocker.field
                          : null
                      }
                      onToggleAdj={() =>
                        setAdjClosed((p) => ({ ...p, [row.uid]: !p[row.uid] }))
                      }
                    />
                  ))}
                </Box>

                <UnstyledButton
                  className={styles.addBtn}
                  onClick={draft.addRow}
                >
                  <IconPlus size={16} stroke={1.8} />
                  <Box component="span">Add another service</Box>
                </UnstyledButton>

                {renderSuggestions()}
              </>
            )}

            {error && (
              <Text className={styles.error} role="alert">
                {error}
              </Text>
            )}
          </Box>
        ) : (
          <MobileBookingReview
            draft={draft}
            onEdit={() => setStep("edit")}
            error={error}
          />
        )}

        {clashBar && (
          <Box className={styles.clashBar} data-testid="mobile-clash-bar">
            <Text component="span" className={styles.barText}>
              {clashBarText(clashNames)}
            </Text>
            <UnstyledButton
              className={styles.barGhost}
              onClick={() => setClashBar(false)}
            >
              Go back
            </UnstyledButton>
            <UnstyledButton
              className={styles.barDanger}
              onClick={handleClashContinue}
            >
              Continue
            </UnstyledButton>
          </Box>
        )}

        {step === "edit" && overlap && !clashBar && (
          <Text
            className={styles.overlap}
            role="status"
            data-testid="mobile-overlap"
          >
            {overlap}
          </Text>
        )}

        <Box className={styles.footer}>
          {step === "edit" ? (
            <>
              {shownBlocker ? (
                <Box className={styles.footBlocker} role="alert">
                  <IconAlertTriangle
                    size={15}
                    className={styles.footBlockerIcon}
                  />
                  <Text component="span" className={styles.footBlockerText}>
                    {shownBlocker.message}
                  </Text>
                </Box>
              ) : (
                <Text className={styles.footHint}>{footHint}</Text>
              )}
              <UnstyledButton
                className={styles.ghostBtn}
                onClick={session.requestClose}
              >
                Cancel
              </UnstyledButton>
              <UnstyledButton className={styles.nextBtn} onClick={handleNext}>
                Next
              </UnstyledButton>
            </>
          ) : (
            <>
              <Box className={styles.footHint} />
              <UnstyledButton
                className={styles.ghostBtn}
                onClick={() => setStep("edit")}
                disabled={draft.submitting}
              >
                Back
              </UnstyledButton>
              <UnstyledButton
                className={styles.confirmBtn}
                onClick={() => void handleConfirm()}
                disabled={draft.submitting}
              >
                Confirm booking
              </UnstyledButton>
            </>
          )}
        </Box>
      </Box>

      {renderPicker()}

      <Modal
        opened={creatingClient}
        onClose={() => setCreatingClient(false)}
        title="Create New Client"
        fullScreen
        transitionProps={{ duration: 0 }}
      >
        <ClientForm
          client={null}
          onClose={(created?: { id?: string } | null) => {
            setCreatingClient(false);
            if (created?.id) {
              draft.setClient(created.id);
              setClientQuery("");
              setPicker(null);
            }
          }}
          showAddCardOption={false}
        />
      </Modal>

      <Modal
        opened={session.discardPromptOpen}
        onClose={session.dismissDiscard}
        withCloseButton={false}
        centered
        size={340}
        radius={14}
        padding={0}
        overlayProps={{ color: "#141414", backgroundOpacity: 0.34, blur: 0 }}
        transitionProps={{ duration: 0 }}
        classNames={{ content: styles.modalContent, body: styles.modalBody }}
        aria-label="Discard this appointment?"
      >
        <Text className={styles.modalTitle}>Discard this appointment?</Text>
        <Text className={styles.modalText}>
          Everything you entered will be lost. This cannot be undone.
        </Text>
        <Box className={styles.modalActions}>
          <UnstyledButton
            className={styles.modalGhost}
            onClick={session.dismissDiscard}
          >
            Keep editing
          </UnstyledButton>
          <UnstyledButton
            className={styles.modalDanger}
            onClick={session.close}
          >
            Discard
          </UnstyledButton>
        </Box>
      </Modal>
    </Box>
  );
};

/**
 * New appointment bottom sheet (README screen 5): draft → review → confirm
 * over the shared booking session. Draggable to dismiss; drag, the close X,
 * the scrim, Cancel and Escape all route through the session's discard gate
 * (prompts only once something was entered). Sits at MOBILE_Z.sheet, below
 * Mantine modals, so the discard confirmation stacks above it.
 */
export const MobileNewAppointmentSheet: React.FC<
  MobileNewAppointmentSheetProps
> = (props) => (
  <Drawer
    opened={props.session.sheetOpen}
    onClose={props.session.requestClose}
    position="bottom"
    withCloseButton={false}
    zIndex={MOBILE_Z.sheet}
    closeOnEscape={false}
    padding={0}
    transitionProps={SHEET_TRANSITION}
    overlayProps={{
      color: "#141414",
      backgroundOpacity: 0.34,
      blur: 0,
      className: sheetMotion.overlay,
    }}
    classNames={{ content: sheetMotion.content }}
    styles={{
      content: {
        height: "auto",
        maxHeight: "none",
        background: "transparent",
        boxShadow: "none",
        overflow: "visible",
      },
      body: { padding: 0 },
    }}
    aria-label="New appointment"
  >
    {props.session.sheetOpen && <SheetBody {...props} />}
  </Drawer>
);
